import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const stopMotionCadenceSkill: Skill = {
    name: SKILL_NAMES.stopMotionCadence,
    kind: 'codeo',
    description:
        'Step motion in discrete held frames instead of interpolating it — the stop-motion or ' +
        'cut-paper cadence, for a handmade register that smooth easing cannot produce.',
    instructions: `# Stop-Motion Cadence

Motion advances in visible steps rather than sliding continuously: an element holds, jumps, holds, jumps. It reads as made by hand — cut paper, claymation, a flipbook — and it is the one cadence that cannot be mistaken for a default easing curve, which is exactly why it reads as authored.

Use it when the piece's design language is craft-flavoured, or to give one element a different character from everything else in the frame.

## Markup

\`\`\`html
<div id="paper-scene" style="position: absolute; inset: 0;">
  <div id="cut-shape" style="position: absolute; left: 300px; top: 380px; width: 320px; height: 320px; border-radius: 24px; background: var(--l5-color-2, #e8552f);"></div>
  <div id="cut-word" style="position: absolute; left: 720px; top: 460px; font-family: var(--l5-font-0), sans-serif; font-size: 128px; font-weight: 900; color: var(--l5-color-0, #1c1a17);">By hand</div>
</div>
\`\`\`

## Timeline code

\`steps(N)\` is a GSAP ease like any other, so the motion is still a pure function of progress and stays seek-safe.

\`\`\`js
const tl = gsap.timeline({ paused: true });

gsap.set('#cut-shape', { x: -60, opacity: 0, rotation: -8 });
gsap.set('#cut-word', { opacity: 0 });

tl.to('#cut-shape', {
    x: 0,
    opacity: 1,
    rotation: 4,
    duration: 0.9,
    ease: 'steps(6)',
}, 0.3);

// The word arrives on its own held frames, offset so the two never step together.
tl.to('#cut-word', { opacity: 1, duration: 0.5, ease: 'steps(3)' }, 0.72);

// A small held wobble keeps the frame alive without smoothing the cadence.
tl.to('#cut-shape', { rotation: -2, duration: 1.6, ease: 'steps(5)' }, 1.4);
tl.to('#cut-shape', { rotation: 3, duration: 1.6, ease: 'steps(5)' }, 3.0);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Step count**: 5–8 steps across a move reads as stop-motion. Below 4 it reads as broken; above about 12 it smooths back out into ordinary motion.
- **Boil**: two or three steps of tiny rotation looping through the scene gives the "boiling" wobble of hand-drawn animation. Keep it finite — a small number of stepped tweens chained, never an infinite repeat.
- **Offset the elements**: if two things step in sync they read as a low frame rate rather than as handmade. Give each a different step count.
- **Paper texture**: pair with grain and a slight drop shadow so the shapes read as physical layers — see \`${SKILL_NAMES.grainAndBloom}\`.
- **Mixed cadence**: one stepped element against otherwise smooth motion makes that element the character in the scene. This is often better than stepping everything.
- **Draw-on in steps**: a dash-drawn stroke with a stepped ease reads as ink applied in strokes — see \`${SKILL_NAMES.svgDrawOn}\`.

## Pitfalls

- Never emulate the cadence with a CSS \`animation\` using \`steps()\`; it runs on wall-clock time and freezes when rasterized. The GSAP ease is the seek-safe version.
- Never use an infinite repeat for a boil. The host seeks the timeline to render each frame, which needs a finite duration.
- Stepped elements are still transform-tweened, so they must not carry a CSS \`transform\`.
- Don't step text that the viewer has to read while it moves. Step it in, then hold it still.
- This is a strong stylistic commitment. In an otherwise smooth, modern piece a stepped element reads as a dropped-frame bug rather than a choice — commit the whole design language or use it on exactly one element.
- Steps and \`back.out\` fight each other. Pick the cadence or pick the overshoot.
- The held frames make timing coarse: with narration, the step that lands on the word has to be placed deliberately, since the motion can only arrive on step boundaries.`,
};
